import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Divider,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useToast,
  Skeleton,
  SkeletonText,
  HStack,
  VStack
} from '@chakra-ui/react';
import { 
  FaStore, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaClock,
  FaShoppingBasket,
  FaInfoCircle,
  FaMapMarked
} from 'react-icons/fa';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api_keys';
import ProductCard from '../../ProductCards/ProductCard';

const StoreDetail = () => {
  const { id } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productsByCategory, setProductsByCategory] = useState({});
  const toast = useToast();

  // Fetch store details and products
  useEffect(() => {
    const fetchStoreAndProducts = async () => {
      setLoading(true);
      try {
        // Fetch store details
        const storeResponse = await axios.get(`${API_BASE_URL}/api/stores/${id}`);
        setStore(storeResponse.data);
        
        // Fetch store products
        const productsResponse = await axios.get(`${API_BASE_URL}/api/stores/${id}/products`);
        console.log('Fetched store products:', productsResponse.data);
        setProducts(productsResponse.data);
        
        // Group products by category
        const groupedProducts = {};
        productsResponse.data.forEach(product => {
          // Check for category in both possible fields and get the name
          const categoryObj = product.category || {};
          const categoryName = (typeof categoryObj === 'object' && categoryObj.name) ? 
                              categoryObj.name : 
                              (typeof categoryObj === 'string' ? categoryObj : 'Other');
          
          if (!groupedProducts[categoryName]) {
            groupedProducts[categoryName] = [];
          }
          groupedProducts[categoryName].push(product);
        });
        setProductsByCategory(groupedProducts);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching store details:', err);
        setError('Failed to load store details. Please try again later.');
        setLoading(false);
        
        toast({
          title: 'Error',
          description: 'Failed to load store details. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    };

    fetchStoreAndProducts();
  }, [id, toast]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box mb={8}>
          <Skeleton height="300px" mb={6} />
          <Flex direction={{ base: 'column', md: 'row' }} mb={6}>
            <Skeleton height="100px" width="100px" borderRadius="full" mr={6} />
            <Box flex="1">
              <SkeletonText mt="4" noOfLines={4} spacing="4" />
            </Box>
          </Flex>
          <SkeletonText mt="4" noOfLines={3} spacing="4" />
        </Box>
        
        <Skeleton height="50px" mb={6} />
        
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={6}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} height="300px" />
          ))}
        </SimpleGrid>
      </Container>
    );
  }

  if (error || !store) {
    return (
      <Container maxW="container.xl" py={8} textAlign="center">
        <Icon as={FaStore} boxSize={12} color="gray.400" mb={4} />
        <Heading size="lg" mb={2}>Store Not Found</Heading>
        <Text mb={6}>{error || 'The store you are looking for does not exist or has been removed.'}</Text>
        <Button as={Link} to="/stores" colorScheme="green">
          Browse All Stores
        </Button>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Store Header with Cover Image */}
      <Box 
        position="relative" 
        height="300px" 
        mb={6} 
        borderRadius="lg" 
        overflow="hidden"
        boxShadow="md"
      >
        {store.coverImage ? (
          <Image 
            src={store.coverImage} 
            alt={store.name} 
            objectFit="cover" 
            width="100%" 
            height="100%" 
          />
        ) : (
          <Flex 
            bg="green.50" 
            width="100%" 
            height="100%" 
            align="center" 
            justify="center"
          >
            <Icon as={FaStore} boxSize={16} color="green.500" />
          </Flex>
        )}
        
        <Box 
          position="absolute" 
          bottom={0} 
          left={0} 
          right={0} 
          bg="blackAlpha.600" 
          p={4}
          color="white"
        >
          <Heading size="lg">{store.name}</Heading>
        </Box>
      </Box>

      {/* Store Info Section */}
      <Flex direction={{ base: 'column', md: 'row' }} mb={8} gap={6}>
        {/* Store Logo and Basic Info */}
        <Box flex="1">
          <Flex mb={4}>
            {store.logo ? (
              <Image 
                src={store.logo} 
                alt={`${store.name} logo`} 
                boxSize="100px" 
                borderRadius="full" 
                mr={4} 
                objectFit="cover"
                border="2px solid"
                borderColor="green.500"
              />
            ) : (
              <Flex 
                boxSize="100px" 
                borderRadius="full" 
                bg="green.100" 
                mr={4}
                align="center"
                justify="center"
                border="2px solid"
                borderColor="green.500"
              >
                <Icon as={FaStore} boxSize={8} color="green.500" />
              </Flex>
            )}
            
            <Box>
              <Heading size="md" mb={2}>{store.name}</Heading>
              <HStack mb={2}>
                <Icon as={FaMapMarkerAlt} color="green.500" />
                <Text fontSize="sm">
                  {store.address.area && `${store.address.area}, `}
                  {store.address.city}, {store.address.state}
                  {store.address.postcode && ` - ${store.address.postcode}`}
                </Text>
              </HStack>
              
              {store.contactPhone && (
                <HStack mb={2}>
                  <Icon as={FaPhone} color="green.500" />
                  <Text fontSize="sm">{store.contactPhone}</Text>
                </HStack>
              )}
              
              {store.contactEmail && (
                <HStack mb={2}>
                  <Icon as={FaEnvelope} color="green.500" />
                  <Text fontSize="sm">{store.contactEmail}</Text>
                </HStack>
              )}
              
              {store.openingHours && (
                <HStack mb={2}>
                  <Icon as={FaClock} color="green.500" />
                  <Text fontSize="sm">{store.openingHours}</Text>
                </HStack>
              )}
            </Box>
          </Flex>
        </Box>
        
        {/* Store Description */}
        <Box flex="2">
          <Heading size="md" mb={2}>
            <Icon as={FaInfoCircle} mr={2} />
            About This Store
          </Heading>
          <Text>{store.description}</Text>
          
          {/* Map Preview (placeholder for future map integration) */}
          {store.location && store.location.coordinates && (
            <Box mt={4}>
              <Heading size="sm" mb={2}>
                <Icon as={FaMapMarked} mr={2} />
                Store Location
              </Heading>
              <Box 
                borderRadius="md" 
                overflow="hidden" 
                height="200px" 
                border="1px solid" 
                borderColor="gray.200"
              >
                <iframe
                  title="Store Location"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3507.743908551197!2d${store.location.coordinates[0]}!3d${store.location.coordinates[1]}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1684765091573!5m2!1sen!2sin`}
                  allowFullScreen=""
                  loading="lazy"
                  style={{border: 0}}
                ></iframe>
              </Box>
            </Box>
          )}
        </Box>
      </Flex>

      <Divider mb={8} />

      {/* Products Section */}
      <Box mb={8}>
        <Heading size="lg" mb={4}>
          <Icon as={FaShoppingBasket} mr={2} />
          Products from {store.name}
        </Heading>
        
        {products.length === 0 ? (
          <Box textAlign="center" p={8} bg="gray.50" borderRadius="md">
            <Text mb={2}>This store hasn't added any products yet.</Text>
            <Text color="gray.600">Check back later for updates.</Text>
          </Box>
        ) : (
          <Tabs colorScheme="green" variant="enclosed">
            <TabList overflowX="auto" flexWrap="nowrap" sx={{
              scrollbarWidth: 'thin',
              '::-webkit-scrollbar': {
                height: '8px',
              },
              '::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
            }}>
              <Tab>All Products ({products.length})</Tab>
              {Object.keys(productsByCategory).map(category => (
                <Tab key={category}>
                  {category} ({productsByCategory[category].length})
                </Tab>
              ))}
            </TabList>
            
            <TabPanels>
              {/* All Products Tab */}
              <TabPanel>
                <SimpleGrid columns={{ base: 2, sm: 2, md: 3, lg: 4 }} spacing={4}>
                  {products.map(product => (
                    <ProductCard key={product._id} product={product} />
                  ))}
                </SimpleGrid>
              </TabPanel>
              
              {/* Category Tabs */}
              {Object.keys(productsByCategory).map(category => (
                <TabPanel key={category}>
                  <SimpleGrid columns={{ base: 2, sm: 2, md: 3, lg: 4 }} spacing={4}>
                    {productsByCategory[category].map(product => (
                      <ProductCard key={product._id} product={product} />
                    ))}
                  </SimpleGrid>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        )}
      </Box>
      
      {/* Back to Stores Button */}
      <Flex justify="center" mt={8}>
        <Button as={Link} to="/stores" colorScheme="green" leftIcon={<FaStore />}>
          Browse All Stores
        </Button>
      </Flex>
    </Container>
  );
};

export default StoreDetail;